import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Info } from 'lucide-react';
import { EDIT_REASONS, fmtTime } from './workbenchShared';
import { ProjectOptions } from './ProjectOptions';

// Edit Session Dialog — adjust clock in/out, re-assign project/job, audit reason.
export const EditSessionDialog = ({
  editing, onClose, form, setForm, onSave, saving,
  projects, projectsLoading, projectsError, onRetryProjects, recentProjectIds,
  jobsCatalog, jobsLoading,
}) => (
  <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="pw-edit-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>Adjust time entry</DialogTitle>
        <DialogDescription>
          {editing?.worker?.worker_name} — {editing?.session?.date}
          {editing?.session?.job_title && <span> · {editing.session.job_title}</span>}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Clock in (local time)</Label>
            <Input
              type="datetime-local"
              value={form.new_clock_in}
              onChange={(e) => setForm((f) => ({ ...f, new_clock_in: e.target.value }))}
              data-testid="pw-edit-clock-in"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Clock out (local time)</Label>
            <Input
              type="datetime-local"
              value={form.new_clock_out}
              onChange={(e) => setForm((f) => ({ ...f, new_clock_out: e.target.value }))}
              data-testid="pw-edit-clock-out"
            />
          </div>
        </div>

        {/* Project / Job re-assignment (Feb 2026) — admins fixing workers who
            clocked into the wrong project or job. */}
        <div className="space-y-1">
          <Label className="text-xs">Project</Label>
          <Select
            value={form.new_project_id || '__none__'}
            onValueChange={(v) => setForm((f) => ({ ...f, new_project_id: v }))}
          >
            <SelectTrigger data-testid="pw-edit-project-select">
              <SelectValue placeholder={projectsLoading ? 'Loading projects…' : 'No project'} />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="__none__" data-testid="pw-edit-project-none">
                No project (clear)
              </SelectItem>
              <ProjectOptions
                projects={projects}
                projectsLoading={projectsLoading}
                projectsError={projectsError}
                onRetry={onRetryProjects}
                recentIds={recentProjectIds}
                testPrefix="pw-edit-project"
                forDate={editing?.session?.date}
              />
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            Current: {editing?.session?.project_name || editing?.session?.project_number || <i>none</i>}
          </p>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Job</Label>
          <Select
            value={form.new_job_id || '__none__'}
            onValueChange={(v) => setForm((f) => ({ ...f, new_job_id: v }))}
          >
            <SelectTrigger data-testid="pw-edit-job-select">
              <SelectValue placeholder={jobsLoading ? 'Loading jobs…' : 'No job'} />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="__none__" data-testid="pw-edit-job-none">
                No job (clear)
              </SelectItem>
              {(jobsCatalog || []).map((j) => (
                <SelectItem key={j.id} value={j.id} data-testid={`pw-edit-job-${j.id}`}>
                  {j.job_number ? `${j.job_number} · ` : ''}{j.title}
                  {j.status && j.status !== 'completed' && (
                    <span className="ml-1 text-[10px] opacity-60">[{j.status}]</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">
            Current: {editing?.session?.job_title || editing?.session?.job_number || <i>none</i>}
          </p>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Reason (audit) *</Label>
          <Select
            value={form.reason_kind}
            onValueChange={(v) => setForm((f) => ({ ...f, reason_kind: v }))}
          >
            <SelectTrigger data-testid="pw-edit-reason-select"><SelectValue placeholder="Pick a reason class…" /></SelectTrigger>
            <SelectContent>
              {EDIT_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`pw-edit-reason-option-${r.value}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            rows={2}
            placeholder="Anything else HR should see (e.g. 'worker confirmed via Slack')…"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="pw-edit-notes"
          />
        </div>
        {editing?.session?.original_clock_in && (
          <div className="text-[11px] text-slate-500 border-t border-[#1F2D3F] pt-2">
            <Info className="w-3 h-3 inline mr-1" />
            Original: in {fmtTime(editing.session.original_clock_in)} · out {fmtTime(editing.session.original_clock_out)}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="pw-edit-cancel">Cancel</Button>
        <Button
          onClick={onSave}
          disabled={saving || !form.reason_kind}
          className="bg-[#1E88E5] hover:bg-blue-700"
          data-testid="pw-edit-save"
        >
          {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</> : 'Save change'}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
